// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useParties } from '../../../contexts/PartiesContext';
import useRequestHolderService from '../../../hooks/mutations/registry/onboarding/useRequestHolderService';
import useMutate from '../../../hooks/mutations/useMutate';
import useHolderServiceRequests from '../../../hooks/queries/registry/onboarding/useHolderServiceRequests';
import useHolderServices from '../../../hooks/queries/registry/onboarding/useHolderServices';
import { ERR_REQ_HOLDER_SVC, SUC_REQ_HOLDER_SVC } from '../../../utils/strings';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
};

export const RequestHolderServiceDialog = ({ open, onClose }: DialogProps) => {
  const [provider, setProvider] = useState<string>('');
  const requestHolderService = useRequestHolderService();
  const mutate = useMutate();

  // Check if service is already requested or provided
  const { operator, party } = useParties();
  const holderServices = useHolderServices();
  const holderServiceRequests = useHolderServiceRequests();
  const hasService = !!holderServices.data?.find(
    (r) => r.payload.provider === provider && r.payload.holder === party,
  );
  const hasPendingRequest = !!holderServiceRequests.data?.find(
    (r) => r.payload.provider === provider && r.payload.holder === party,
  );

  const resetState = useCallback(() => {
    setProvider('');
  }, [setProvider]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    const payload = {
      provider,
      operator,
      party,
    };
    const res = await mutate(requestHolderService, payload, SUC_REQ_HOLDER_SVC, ERR_REQ_HOLDER_SVC);
    handleClose();
    return res;
  }, [requestHolderService, provider, operator, party, handleClose, mutate]);

  const isLoading = false;
  if (isLoading) return null;

  const disableSubmit = !provider || hasService || hasPendingRequest;
  return (
    <FormDialog
      open={open}
      title="Request Holder Service"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput label="Provider" value={provider} setValue={setProvider} required />
    </FormDialog>
  );
};
